import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { logoutFromSSO } from '../utils/msal';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_API_URL || process.env.API_URL || 'http://localhost:8000';
const CDN_URL = process.env.REACT_APP_CDN_URL || process.env.CDN_URL || 'https://storage.googleapis.com/smod-branding';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState(null);

  // Configure axios defaults
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  useEffect(() => {
      const initializeAuth = async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
        setToken(storedToken);
        
        // Check if token has impersonation info
        try {
          const payload = JSON.parse(atob(storedToken.split('.')[1]));
          if (payload.is_impersonating) {
            setIsImpersonating(true);
            setOriginalUser({
              name: payload.original_user_name,
              email: payload.original_user_email,
              id: payload.original_user_id
            });
          }
        } catch (tokenError) {
          console.error('Error parsing token:', tokenError);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        
        // If we're on an admin page and token is invalid, redirect to login
        if (window.location.pathname.includes('/admin')) {
          window.location.href = '/login';
          return;
        }
      }
    }
    setLoading(false);
  };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });

      const { access_token, user_id, ...userData } = response.data;
      
      // Map user_id to id for consistency with UserResponse model
      const userDataWithId = {
        id: user_id,
        ...userData
      };
      
      setToken(access_token);
      setUser(userDataWithId);
      
      // Handle impersonation data
      if (userDataWithId.is_impersonating) {
        setIsImpersonating(true);
        setOriginalUser({
          name: userDataWithId.original_user_name,
          email: userDataWithId.original_user_email
        });
      } else {
        setIsImpersonating(false);
        setOriginalUser(null);
      }
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true, user: userDataWithId };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const loginWithSSO = async (idToken) => {
    try {
      const response = await axios.post(`${API_URL}/auth/sso-login`, {
        id_token: idToken
      });

      const { access_token, user_id, ...userData } = response.data;

      const userDataWithId = {
        id: user_id,
        ...userData
      };

      setToken(access_token);
      setUser(userDataWithId);
      setIsImpersonating(false);
      setOriginalUser(null);

      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true, user: userDataWithId };
    } catch (error) {
      console.error('SSO login failed:', error);
      const data = error.response?.data;
      return {
        success: false,
        error: data?.message || data?.detail || 'SSO login failed',
        code: data?.code || null,
        email: data?.email || null
      };
    }
  };

  const logout = async () => {
    try {
      // Call the logout API endpoint to log the logout activity
      if (token) {
        await axios.post(`${API_URL}/auth/logout`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      // Don't prevent logout if API call fails
      console.error('Logout API call failed:', error);
    } finally {
      // Always perform local cleanup regardless of API call success
      setUser(null);
      setToken(null);
      setIsImpersonating(false);
      setOriginalUser(null);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];

      // If user logged in via SSO, also log out from Microsoft Entra ID
      const ssoLogoutInitiated = await logoutFromSSO();
      if (ssoLogoutInitiated) {
        // logoutFromSSO redirects to Microsoft then back to /login - no further action needed
        return;
      }
    }
  };

  const startImpersonation = async (userId) => {
    try {
      const response = await axios.post(`${API_URL}/auth/impersonate/${userId}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const { access_token, user_id, ...userData } = response.data;
      
      // Map user_id to id for consistency
      const userDataWithId = {
        id: user_id,
        ...userData
      };
      
      setToken(access_token);
      setUser(userDataWithId);
      setIsImpersonating(true);
      setOriginalUser({
        name: userDataWithId.original_user_name,
        email: userDataWithId.original_user_email
      });
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true, user: userDataWithId };
    } catch (error) {
      console.error('Impersonation failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Impersonation failed' 
      };
    }
  };

  const stopImpersonation = async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/stop-impersonate`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const { access_token, ...userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      setIsImpersonating(false);
      setOriginalUser(null);
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Stop impersonation failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to stop impersonation' 
      };
    }
  };

  const isAuthenticated = () => {
    return !!user && !!token;
  };

  const isSuperAdmin = () => {
    return user?.role === 'SuperAdmin';
  };

  const isSales = () => {
    return user?.role === 'Sales';
  };

  const canAccessAdminPanel = () => {
    return user?.role === 'SuperAdmin' || user?.role === 'Sales';
  };

  const isAdminOrManager = () => {
    return user?.role && ['Admin', 'Manager'].includes(user.role);
  };

  const isDealer = () => {
    return user?.role === 'Dealer';
  };

  // API functions for admin panel
  const apiCall = async (method, endpoint, data = null, customHeaders = {}) => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
      };

      // Don't set Content-Type for FormData - let browser set it with proper boundary
      if (!(data instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      // Merge with custom headers
      Object.assign(headers, customHeaders);

      const config = {
        method,
        url: `${API_URL}${endpoint}`,
        headers,
      };

      if (data) {
        config.data = data;
      }

      const response = await axios(config);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('API call failed:', error);
      
      // Handle authentication errors (401 Unauthorized)
      if (error.response?.status === 401) {
        // Check if this is a Tremendous OAuth error (not a user auth error)
        const errorDetail = error.response?.data?.detail || '';
        const isTremendousError = endpoint.includes('/tremendous/') || 
                                  errorDetail.toLowerCase().includes('tremendous') ||
                                  errorDetail.toLowerCase().includes('oauth');
        
        if (isTremendousError) {
          // This is a Tremendous OAuth error, not a user authentication error
          // Don't logout the user, just return the error
          console.log('Tremendous OAuth error - not logging out user');
          return {
            success: false,
            error: errorDetail || 'Tremendous authentication error',
            requiresAuth: false
          };
        }
        
        // This is a user authentication error - logout
        console.log('Authentication failed - logging out user');
        logout();
        // Redirect to login page
        window.location.href = '/login';
        return { 
          success: false, 
          error: 'Authentication failed',
          requiresAuth: true
        };
      }
      
      // Handle different error response formats
      let errorMessage = 'API call failed';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            // Handle FastAPI validation errors
            errorMessage = error.response.data.detail
              .map(err => `${err.loc.join('.')}: ${err.msg}`)
              .join('; ');
          } else {
            errorMessage = JSON.stringify(error.response.data.detail);
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  // Function to fetch balance from SMOD API via backend proxy
  const fetchSmodBalance = async (companyId) => {
    try {
      const response = await axios.get(`${API_URL}/api/smod/balance/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('SMOD API call failed:', error);
      
      // Handle authentication errors (401 Unauthorized)
      if (error.response?.status === 401) {
        console.log('Authentication failed - logging out user');
        logout();
        window.location.href = '/login';
        return { 
          success: false, 
          error: 'Authentication failed',
          requiresAuth: true
        };
      }
      
      let errorMessage = 'Failed to fetch balance';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    isImpersonating,
    originalUser,
    login,
    loginWithSSO,
    logout,
    startImpersonation,
    stopImpersonation,
    isAuthenticated,
    isSuperAdmin,
    isSales,
    canAccessAdminPanel,
    isAdminOrManager,
    isDealer,
    apiCall,
    fetchSmodBalance,
    API_URL,
    CDN_URL
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 